import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import moment from "moment";
import { RecaptchaComponent } from 'ng-recaptcha';
import { UsersService } from "../../services/users.service";
import { StorageService } from "../../services/storage.service";

@Component({
    selector: "app-login-component",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
    readonly recaptchaSiteKey = '6Ld6tE0tAAAAAAzSAd6n07LSSQqPLVY7zGf5FKsS';
    private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    private readonly invalidEmailMessage = 'Please enter a valid email address.';
    private readonly invalidCaptchaMessage = 'Incorrect CAPTCHA answer. Please try again.';
    private readonly verificationCodeTtlMs = 30 * 60 * 1000;
    private readonly verificationMaxAttempts = 5;
    private readonly loginMaxAttempts = 5;
    private readonly loginLockDurationMs = 20 * 60 * 1000;

    error = false;
    errorMessage = 'Login Failed';
    email = '';
    password = '';
    showPassword = false;
    submitting = false;
    failedAttempts = 0;
    secondsUntilUnlock = 0;
    copyright = "© " + moment().format('YYYY') + " saferoads.ph version 1.9 Powered by Reviewmasters Inc.";

    private lockedEmail = '';
    private currentCaptchaResult = 0;
    private statusTimerId: number | null = null;
    private pendingLogin: { email: string; password: string } | null = null;

    @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;

    constructor(
        private userService: UsersService,
        private storageService: StorageService,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.startStatusTimer();
    }

    ngOnDestroy(): void {
        this.stopStatusTimer();
    }

    async login() {
        if (this.submitting) {
            return;
        }

        const normalizedEmail = this.email.trim();

        if (!normalizedEmail || !this.password) {
            this.error = true;
            this.errorMessage = 'Email and password are required.';
            return;
        }

        if (!this.emailPattern.test(normalizedEmail)) {
            this.error = true;
            this.errorMessage = this.invalidEmailMessage;
            return;
        }

        await this.refreshLockoutState(normalizedEmail);
        if (this.isLocked()) {
            this.error = true;
            this.errorMessage = this.buildLockoutMessage();
            this.cdr.detectChanges();
            return;
        }

        this.error = false;
        this.submitting = true;
        this.pendingLogin = { email: normalizedEmail, password: this.password };
        this.captchaRef.execute();
    }

    async onCaptchaResolved(token: string | null): Promise<void> {
        // Ignore expiry/reset callbacks that fire with no active submission
        if (!this.pendingLogin) {
            this.submitting = false;
            return;
        }

        if (!token) {
            this.captchaRef.reset();
            this.error = true;
            this.errorMessage = this.invalidCaptchaMessage;
            this.submitting = false;
            this.pendingLogin = null;
            this.cdr.detectChanges();
            return;
        }

        let credentialsAccepted = false;
        const login = this.pendingLogin;
        this.pendingLogin = null;

        try {
            const response: any = await this.userService.loginV2(login);

            if (typeof response?.statusCode === 'number' && response.statusCode !== 200) {
                throw new Error('Login Failed');
            }

            if (response?.success === false) {
                throw new Error('Login Failed');
            }

            credentialsAccepted = true;
            await this.clearLoginAttempts(login.email);
            await this.storageService.set("email", login.email);
            await this.userService.sendLoginAuth(login);
            await this.storageService.set('mfaVerified', 'false');
            await this.storageService.set('mfaCodeExpiresAt', String(Date.now() + this.verificationCodeTtlMs));
            await this.storageService.set('mfaAttemptsRemaining', String(this.verificationMaxAttempts));
            await this.storageService.set('mfaLockedUntil', '');
            await this.router.navigate(['/auth/verification'], {
                state: {
                    login,
                },
            });
        } catch (err: unknown) {
            if (!credentialsAccepted) {
                await this.recordFailedAttempt(login.email);
            }
            this.error = true;
            this.errorMessage = this.resolveLoginErrorMessage(err);
            console.error('Login error:', err);
            this.cdr.detectChanges();
        } finally {
            if (!credentialsAccepted) {
                this.captchaRef.reset();
            }
            this.submitting = false;
            this.cdr.detectChanges();
        }
    }

    validateEmailOnBlur(): void {
        const normalizedEmail = this.email.trim();

        if (!normalizedEmail) {
            this.resetDisplayedLockoutState();
            if (this.error && this.errorMessage === this.invalidEmailMessage) {
                this.error = false;
            }
            return;
        }

        if (!this.emailPattern.test(normalizedEmail)) {
            this.error = true;
            this.errorMessage = this.invalidEmailMessage;
            return;
        }

        void this.refreshLockoutState(normalizedEmail).then(() => {
            if (this.error && this.errorMessage === this.invalidEmailMessage) {
                this.error = false;
            }

            this.syncLoginAttemptErrorState();

            this.cdr.detectChanges();
        });

        if (this.error && this.errorMessage === this.invalidEmailMessage) {
            this.error = false;
        }
    }

    onEmailInputChange(): void {
        const normalizedEmail = this.email.trim();

        if (!normalizedEmail) {
            this.resetDisplayedLockoutState();
            return;
        }

        if (!this.emailPattern.test(normalizedEmail)) {
            return;
        }

        void this.refreshLockoutState(normalizedEmail).then(() => {
            this.syncLoginAttemptErrorState();

            this.cdr.detectChanges();
        });
    }

    onCaptchaInputChange(): void {
        if (this.error && this.errorMessage === this.invalidCaptchaMessage) {
            this.error = false;
            this.errorMessage = 'Login Failed';
        }
    }

    isLocked(): boolean {
        return this.secondsUntilUnlock > 0;
    }

    get attemptsRemaining(): number {
        return Math.max(0, this.loginMaxAttempts - this.failedAttempts);
    }

    get formattedCountdown(): string {
        const m = Math.floor(this.secondsUntilUnlock / 60);
        const s = this.secondsUntilUnlock % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    private resolveLoginErrorMessage(err: unknown): string {
        if (this.isLocked()) {
            return this.buildLockoutMessage();
        }

        if (this.failedAttempts > 0) {
            return this.buildAttemptsRemainingMessage();
        }

        if (err instanceof HttpErrorResponse) {
            return 'Login Failed';
        }

        if (err instanceof Error) {
            return err.message || 'Login Failed';
        }

        return 'Login Failed';
    }

    private async recordFailedAttempt(email: string): Promise<void> {
        const attemptsKey = this.getLoginAttemptsKey(email);
        const lockedUntilKey = this.getLoginLockedUntilKey(email);
        const failedAttemptsRaw = await this.storageService.get(attemptsKey);
        const lockedUntilRaw = await this.storageService.get(lockedUntilKey);
        const lockedUntilMs = this.parseEpoch(lockedUntilRaw);
        const remainingSeconds = this.computeRemainingSeconds(lockedUntilMs);

        if (remainingSeconds > 0) {
            this.lockedEmail = email;
            this.failedAttempts = this.loginMaxAttempts;
            this.secondsUntilUnlock = remainingSeconds;
            return;
        }

        const nextFailedAttempts = this.parseCount(failedAttemptsRaw) + 1;
        this.lockedEmail = email;
        this.failedAttempts = nextFailedAttempts;
        await this.storageService.set(attemptsKey, String(nextFailedAttempts));

        if (nextFailedAttempts >= this.loginMaxAttempts) {
            const lockedUntilMsNext = Date.now() + this.loginLockDurationMs;
            this.secondsUntilUnlock = this.computeRemainingSeconds(lockedUntilMsNext);
            await this.storageService.set(lockedUntilKey, String(lockedUntilMsNext));
            return;
        }

        this.secondsUntilUnlock = 0;
        await this.storageService.set(lockedUntilKey, '');
    }

    private async clearLoginAttempts(email: string): Promise<void> {
        await this.storageService.set(this.getLoginAttemptsKey(email), '0');
        await this.storageService.set(this.getLoginLockedUntilKey(email), '');

        if (this.lockedEmail === email) {
            this.resetDisplayedLockoutState();
        }
    }

    private async refreshLockoutState(email: string): Promise<void> {
        const attemptsKey = this.getLoginAttemptsKey(email);
        const lockedUntilKey = this.getLoginLockedUntilKey(email);
        const failedAttemptsRaw = await this.storageService.get(attemptsKey);
        const lockedUntilRaw = await this.storageService.get(lockedUntilKey);
        const failedAttempts = this.parseCount(failedAttemptsRaw);
        const lockedUntilMs = this.parseEpoch(lockedUntilRaw);
        const remainingSeconds = this.computeRemainingSeconds(lockedUntilMs);

        this.lockedEmail = email;
        this.failedAttempts = failedAttempts;
        this.secondsUntilUnlock = remainingSeconds;

        // Only reset when a lockout was previously set and has now expired
        if (remainingSeconds === 0 && lockedUntilMs > 0) {
            await this.storageService.set(attemptsKey, '0');
            await this.storageService.set(lockedUntilKey, '');
            this.failedAttempts = 0;
        }
    }

    private resetDisplayedLockoutState(): void {
        this.lockedEmail = '';
        this.failedAttempts = 0;
        this.secondsUntilUnlock = 0;

        if (this.isLoginAttemptErrorMessage(this.errorMessage)) {
            this.error = false;
            this.errorMessage = 'Login Failed';
        }
    }

    private buildLockoutMessage(): string {
        const minutesUntilUnlock = Math.ceil(this.secondsUntilUnlock / 60);
        return `Too many failed login attempts. Try again in ${minutesUntilUnlock} minute(s).`;
    }

    private buildAttemptsRemainingMessage(): string {
        const attemptsRemaining = Math.max(0, this.loginMaxAttempts - this.failedAttempts);
        return `Login Failed. ${attemptsRemaining} attempt(s) remaining before a 20-minute lock.`;
    }

    private syncLoginAttemptErrorState(): void {
        if (this.isLocked()) {
            this.error = true;
            this.errorMessage = this.buildLockoutMessage();
            return;
        }

        if (this.failedAttempts > 0) {
            this.error = true;
            this.errorMessage = this.buildAttemptsRemainingMessage();
            return;
        }

        if (this.isLoginAttemptErrorMessage(this.errorMessage)) {
            this.error = false;
            this.errorMessage = 'Login Failed';
        }
    }

    private isLoginAttemptErrorMessage(message: string): boolean {
        return message.startsWith('Login Failed. ') || message.startsWith('Too many failed login attempts.');
    }

    private computeRemainingSeconds(targetEpochMs: number): number {
        if (!targetEpochMs || targetEpochMs <= 0) {
            return 0;
        }

        const diffMs = targetEpochMs - Date.now();
        if (diffMs <= 0) {
            return 0;
        }

        return Math.ceil(diffMs / 1000);
    }

    private parseEpoch(value: string | null): number {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    private parseCount(value: string | null): number {
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0) {
            return 0;
        }

        return Math.min(this.loginMaxAttempts, Math.floor(parsed));
    }

    private getLoginAttemptsKey(email: string): string {
        return `loginFailedAttempts:${email.toLowerCase()}`;
    }

    private getLoginLockedUntilKey(email: string): string {
        return `loginLockedUntil:${email.toLowerCase()}`;
    }

    private startStatusTimer(): void {
        this.stopStatusTimer();
        this.statusTimerId = window.setInterval(() => {
            const normalizedEmail = this.email.trim();
            if (!normalizedEmail || !this.emailPattern.test(normalizedEmail)) {
                return;
            }

            void this.refreshLockoutState(normalizedEmail).then(() => {
                this.syncLoginAttemptErrorState();

                this.cdr.detectChanges();
            });
        }, 1000);
    }

    private stopStatusTimer(): void {
        if (this.statusTimerId != null) {
            window.clearInterval(this.statusTimerId);
            this.statusTimerId = null;
        }
    }
}